import * as React from 'react';
import '../../Css/Global/Global.css';
import InfoCard from '../Dashboard/InfoCard';
import { Grid } from "@mui/material";
import { getSafeNumber } from '../../Helpers';

import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BalanceIcon from '@mui/icons-material/Balance';

import BarChart from './BarChart';
import SpedStatusHandler from '../Dashboard/SpedStatusHandler';
import TableComponent from '../../Components/TableComponent';
import EvolucaoIcmsChart from './EvolucaoIcmsChart';
import TopChartWithTable from '../../Components/TopChartWithTable';
import IcmsPorSecaoChart from './IcmsPorSecaoChart';
import IcmsHierarchicalTable from './IcmsHierarchicalTable';
import CardSection from '../../Components/CardSection';
import ProductDrillDownModal from '../../Components/ProductDrillDownModal';
import { useProductDrillDown } from '../../Hooks/useProductDrillDown';

import { DadoIcms } from './types';

interface Subgrupo {
  descricao: string;
  icms_total: number;
}

interface Grupo {
  descricao: string;
  icms_total: number;
  subgrupos: Subgrupo[];
}

interface Secao {
  descricao: string;
  icms_total: number;
  grupos: Grupo[];
}
  

const Analises: React.FC = () => {

    const [loading, setLoading] = React.useState(true);

    const [spedFiscalIdSelecionado, setSpedFiscalIdSelecionado] = React.useState<number | null>(null);

    // Hook para drilldown de produtos
    const {
        isModalOpen: isProductModalOpen,
        drillDownData: productDrillDownData,
        loading: productDrillDownLoading,
        selectedProductName,
        openModal: openProductModal,
        closeModal: closeProductModal,
        error: productDrillDownError
    } = useProductDrillDown();

    const [icmsRecolher, setIcmsRecolher] = React.useState<number | null>(null);
    const [ipi, setIpi] = React.useState<number | null>(null);
        
    const [creditosTotaisIcms, setCreditosTotaisIcms] = React.useState<number | null>(null);
    const [ajustesCredito, setAjustesCredito] = React.useState<number | null>(null);

    const [debitosTotaisIcms, setDebitosTotaisIcms] = React.useState<number | null>(null);
    const [ajustesDebito, setAjustesDebito] = React.useState<number | null>(null);

    const [estornosDebito, setEstornosDebito] = React.useState<number | null>(null);
    const [totalDeducoes, setTotalDeducoes] = React.useState<number | null>(null);
    const [estornosCredito, setEstornosCredito] = React.useState<number | null>(null);
    const [saldoCredorAnterior, setSaldoCredorAnterior] = React.useState<number | null>(null);

    const [saldoApuradoPeriodo, setSaldoApuradoPeriodo] = React.useState<number | null>(null);
    const [saldoCredorTransportar, setSaldoCredorTransportar] = React.useState<number | null>(null);
    const [debitosEspeciais, setDebitosEspeciais] = React.useState<number | null>(null);

    const [ajustesIcmsLista, setAjustesIcmsLista] = React.useState([]);
    
    const [dadosIcms, setDadosIcms] = React.useState<DadoIcms[]>([]);

    const [icmsHierarquico, setIcmsHierarquico] = React.useState<Secao[]>([]);
    const [dadosIcmsProdutos, setDadosIcmsProdutos] = React.useState<any[]>([]);

    const [establishmentId, setEstablishmentId] = React.useState<number | null>(null);


    // Função para buscar dados de ICMS totais
    const fetchIcmsTotais = async (spedFiscalId: number) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}icms-totais/${spedFiscalId}?sped_fiscal_id=${spedFiscalId}`);
        const data = await response.json();
        return data;
    };

    // Função para buscar ajustes de ICMS
    const fetchAjustesIcms = async (spedFiscalId: number) => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/getAjustesIcms/${spedFiscalId}`);
        const data = await response.json();
        return data;
    };

    // Função para buscar dados hierárquicos de ICMS
    const fetchIcmsHierarquico = async (spedFiscalId: number): Promise<Secao[]> => {
        const token = document.cookie
            .split('; ')
            .find((row) => row.startsWith('authTokenMHP='))
            ?.split('=')[1];
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/icms-hierarquico/${spedFiscalId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
            }
        });
        let data: unknown = null;

        try {
            data = await response.json();
        } catch (error) {
            data = null;
        }

        const hasMessage =
            typeof data === 'object' &&
            data !== null &&
            'message' in (data as Record<string, unknown>);

        if (!response.ok) {
            const message = hasMessage
                ? String((data as Record<string, unknown>).message)
                : `Erro ao carregar hierarquia de ICMS (status ${response.status})`;
            throw new Error(message);
        }

        if (!Array.isArray(data)) {
            console.warn('Resposta inesperada para icms-hierarquico:', data);
            return [];
        }

        return data as Secao[];
    };

    // Função para processar dados de ICMS totais e atualizar estados
    const processIcmsTotaisData = (totalData: any) => {
        // DEIXAR SEMPRE AS DEFINIÇÕES NA MESMA ORDEM DOS CARDS PARA FACILITAR A MANUTENÇÃO
        setIcmsRecolher(getSafeNumber(totalData, 'icms_totais.vl_icms_recolher')); //ICMS a Recolher
        setIpi(getSafeNumber(totalData, 'ipi')); //IPI
        setCreditosTotaisIcms(getSafeNumber(totalData, 'icms_totais.vl_tot_creditos')); //Créditos Totais de ICMS
        setAjustesCredito(getSafeNumber(totalData, 'icms_totais.vl_aj_creditos')); //Ajustes a Crédito
        setDebitosTotaisIcms(getSafeNumber(totalData, 'icms_totais.vl_tot_debitos')); //Débitos Totais de ICMS
        setAjustesDebito(getSafeNumber(totalData, 'icms_totais.vl_aj_debitos')); //Ajustes a Débito
        setEstornosDebito(getSafeNumber(totalData, 'icms_totais.vl_estornos_deb')); //Estornos de Débito
        setTotalDeducoes(getSafeNumber(totalData, 'icms_totais.vl_tot_deducoes')); //Total de Deduções     
        setEstornosCredito(getSafeNumber(totalData, 'icms_totais.vl_estornos_cred')); //Estornos de Crédito
        setSaldoCredorAnterior(getSafeNumber(totalData, 'icms_totais.vl_sld_credor_ant')); //Saldo Credor Anterior
        setSaldoApuradoPeriodo(getSafeNumber(totalData, 'icms_totais.vl_sld_apurado')); //Saldo Apurado no Período
        setSaldoCredorTransportar(getSafeNumber(totalData, 'icms_totais.vl_sld_credor_transportar')); //Saldo Credor a Transportar
        setDebitosEspeciais(getSafeNumber(totalData, 'icms_totais.deb_esp')); //Outros Débitos de Ajuste

        // Dados formatados dinamicamente para o gráfico
        setDadosIcms([
            {
                tipo: 'ICMS',
                'CRÉDITOS TOTAIS DE ICMS': getSafeNumber(totalData, 'icms_totais.vl_tot_creditos'),
                'DÉBITOS TOTAIS DE ICMS': getSafeNumber(totalData, 'icms_totais.vl_tot_debitos')
            }
        ]);
    };

    // Adicionar nova função de busca de dados de ICMS por seção
    const fetchIcmsPorSecao = async (spedFiscalId: number) => {
        const token = document.cookie
            .split('; ')
            .find((row) => row.startsWith('authTokenMHP='))
            ?.split('=')[1];
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/icms-por-secao/${spedFiscalId}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json'
            }
        });

        const json = await response.json();

        // Transformar dados para o formato do TopChartWithTable
        return json.data.map((item: any) => ({
            Produto: item["Seção"],
            ICMS: item["ICMS"]
        })).sort((a: any, b: any) => b.ICMS - a.ICMS);
    };

    // Adicionar estado para dados de ICMS por seção
    const [dadosIcmsPorSecao, setDadosIcmsPorSecao] = React.useState<any[]>([]);

    React.useEffect(() => {
        if (Number(spedFiscalIdSelecionado) > 0 && establishmentId != null){
            const fetchData = async () => {
                setLoading(true);
                try {
                    // Executar requisições independentes em paralelo
                    const token = document.cookie
                        .split('; ')
                        .find((row) => row.startsWith('authTokenMHP='))
                        ?.split('=')[1];

                    const [totalData, ajustesIcmsData, produtosIcmsResponse] = await Promise.all([
                        fetchIcmsTotais(spedFiscalIdSelecionado!),
                        fetchAjustesIcms(spedFiscalIdSelecionado!),
                        fetch(`${process.env.REACT_APP_API_URL}sped-fiscal/icms-produtos/${spedFiscalIdSelecionado}`, {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        })
                    ]);

                    // Processar dados de ICMS totais
                    processIcmsTotaisData(totalData);
                    
                    // Processar dados de ajustes ICMS
                    setAjustesIcmsLista(ajustesIcmsData.ajustes);

                    // Processar dados de produtos ICMS
                    const produtosIcmsData = await produtosIcmsResponse.json();
                    if (produtosIcmsData.data && Array.isArray(produtosIcmsData.data)) {
                        setDadosIcmsProdutos(produtosIcmsData.data);
                    }

                    // Buscar dados hierárquicos (requisição separada que precisa de token)
                    const icmsHierarquicoData = await fetchIcmsHierarquico(spedFiscalIdSelecionado!);
                    setIcmsHierarquico(icmsHierarquicoData);

                    // Adicionar busca de dados de ICMS por seção
                    const icmsPorSecaoData = await fetchIcmsPorSecao(spedFiscalIdSelecionado!);
                    setDadosIcmsPorSecao(icmsPorSecaoData);

                } catch (e) {
                    console.error('Erro ao carregar dados do dashboard', e);
                    setIcmsHierarquico([]);
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }

    }, [spedFiscalIdSelecionado]);

    // Função para lidar com clique em produto no gráfico
    const handleProductClick = React.useCallback(async (product: any) => {
        if (spedFiscalIdSelecionado && product.cod_item) {
            await openProductModal(
                product.Produto || product.descricao_produto || 'Produto',
                product.cod_item,
                spedFiscalIdSelecionado
            );
        }
    }, [spedFiscalIdSelecionado, openProductModal]);

    return (
        <>
            <Grid container spacing={0.5}>
                <SpedStatusHandler
                    onSpedFiscalIdChange={(id, dataInicio, dataFim, establishmentId) => {
                        setSpedFiscalIdSelecionado(id);
                        setEstablishmentId(establishmentId ? Number(establishmentId) : null);
                    }}
                />

                <CardSection 
                    title="Apuração de Impostos" 
                    icon={<MonetizationOnIcon />} 
                    iconColor="info"
                >
                    <InfoCard
                        title="ICMS a Recolher"
                        value={icmsRecolher}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="IPI"
                        value={ipi}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />
                </CardSection>

                <CardSection 
                    title="Créditos" 
                    icon={<MonetizationOnIcon />} 
                    iconColor="success"
                    size={6}
                >
                    <InfoCard
                        title="Créditos Totais de ICMS"
                        value={creditosTotaisIcms}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Ajustes a Crédito"
                        value={ajustesCredito}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Estornos de Crédito"
                        value={estornosCredito}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Total de Deduções"
                        value={totalDeducoes}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />
                </CardSection>

                <CardSection 
                    title="Débitos" 
                    icon={<MonetizationOnIcon />} 
                    iconColor="error"
                    size={6}
                >
                    <InfoCard
                        title="Débitos Totais de ICMS"
                        value={debitosTotaisIcms}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Ajustes a Débito"
                        value={ajustesDebito}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Estornos de Débito"
                        value={estornosDebito}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />

                    <InfoCard
                        title="Saldo Credor Anterior"
                        value={saldoCredorAnterior}
                        formatAsCurrency={true}
                        size={6}
                        loading={loading}
                    />
                </CardSection>

                <CardSection 
                    title="Saldos" 
                    icon={<MonetizationOnIcon />} 
                    iconColor="info" 
                    size={12}
                >
                    <InfoCard
                        title="Saldo Apurado no Período"
                        value={saldoApuradoPeriodo}
                        formatAsCurrency={true}
                        size={4}
                        loading={loading}
                    />

                    <InfoCard
                        title="Saldo Credor a Transportar"
                        value={saldoCredorTransportar}
                        formatAsCurrency={true}
                        size={4}
                        loading={loading}
                    />

                    <InfoCard
                        title="Outros Débitos de Ajuste"
                        value={debitosEspeciais}
                        formatAsCurrency={true}
                        size={4}
                        loading={loading}
                    />

                    <TableComponent
                        title="Detalhes do Ajuste no ICMS"
                        value={ajustesIcmsLista}
                        noCard={true}
                        loading={loading}
                        fixedHeight={300}
                    />

                </CardSection>
                
                <Grid item xs={12} md={12} lg={6}>
                    <BarChart
                        data={dadosIcms}
                        title="Comparativo entre Créditos e Débitos Totais de ICMS"
                        loading={loading}
                    />
                </Grid>

                <Grid item xs={12} md={12} lg={6}>
                    <TopChartWithTable
                        data={dadosIcmsProdutos}
                        loading={loading}
                        title="Saldo de ICMS por Produto (Top 10)"
                        color="#ff5252"
                        tableTitle="Demais Produtos com ICMS"
                        nameKey="Produto"
                        valueKey="ICMS"
                        showTotal={true}
                        onItemClick={handleProductClick}
                        useDynamicColors={true}
                    />
                </Grid>

                <Grid item xs={12} md={12} lg={6}>
                    <TopChartWithTable
                        data={dadosIcmsPorSecao}
                        loading={loading}
                        title="Saldo de ICMS por Seção"
                        color="#ff5252"
                        tableTitle="Demais Seções com ICMS"
                        nameKey="Produto"
                        valueKey="ICMS"
                        showTotal={true}
                        onItemClick={handleProductClick}
                    />
                </Grid>

                <Grid item xs={12} md={12} lg={6}>
                    <IcmsHierarchicalTable data={icmsHierarquico} title={"Saldo de ICMS por Seção, Grupo e Subgrupo"} loading={loading} />
                </Grid>

                <Grid item xs={12} md={12} lg={3}>
                    {establishmentId != null &&
                        <EvolucaoIcmsChart
                            endpoint={"icms-evolucao/3?establishment_id="+establishmentId}
                            title={"Evolução do ICMS a Recolher (Últimos 3 meses)"}
                        />
                    }
                </Grid>
                <Grid item xs={12} md={12} lg={9}>
                    {establishmentId != null &&
                        <EvolucaoIcmsChart
                            endpoint={"icms-evolucao/12?establishment_id="+establishmentId}
                            title={"Evolução do ICMS a Recolher (Últimos 12 meses)"}
                        />
                    }
                </Grid>
            </Grid>

            {/* Modal de drilldown de produtos */}
            <ProductDrillDownModal
                open={isProductModalOpen}
                onClose={closeProductModal}
                data={productDrillDownData}
                loading={productDrillDownLoading}
                productName={selectedProductName}
            />
        </>
      );

}

interface Subgrupo {
  descricao: string;
  icms_total: number;
}

interface Grupo {
  descricao: string;
  icms_total: number;
  subgrupos: Subgrupo[];
}

interface Secao {
  descricao: string;
  icms_total: number;
  grupos: Grupo[];
}
export default Analises;

